import { flattenAttributes, groupAttributesByType, type MasterProduct } from 'apis/master-products';
import { UOM_HELP } from 'pages/inquiry-processor/components/master-product-families/uom-field-meta';
import type { BulkUpsertVariantRow, EditableVariantRow, RowValidationError } from './editable-variant-row.types';

let counter = 0;
function nextClientId(): string {
  counter += 1;
  return `variant-row-${counter}`;
}

// Shared with variant-row.tsx's per-cell tooltip filtering, so the tooltip text always matches
// what validateRows actually produced (and substring-matching a message here can't silently
// drift out of sync with the string a cell is looking for).
export const REQUIRED_MESSAGES = {
  state: 'State is required',
  brand: 'Brand is required',
  baseUnit: 'Fundamental Unit is required',
} as const;

export function createEmptyRow(): EditableVariantRow {
  return {
    clientId: nextClientId(),
    id: undefined,
    state: '',
    brand: '',
    origin: '',
    baseUnit: '',
    itemUnit: '',
    netContent: '',
    packUnit: '',
    packSize: '',
    spec: '',
    isActive: true,
    attributes: {},
  };
}

export function mapVariantToRow(variant: MasterProduct): EditableVariantRow {
  return {
    clientId: nextClientId(),
    id: variant.id,
    state: variant.state ?? '',
    brand: variant.brand ?? '',
    origin: variant.origin ?? '',
    baseUnit: variant.baseUnit ?? '',
    itemUnit: variant.itemUnit ?? '',
    netContent: variant.netContent != null ? String(variant.netContent) : '',
    packUnit: variant.packUnit ?? '',
    packSize: variant.packSize != null ? String(variant.packSize) : '',
    spec: variant.spec ?? '',
    isActive: variant.isActive,
    // Collects ALL values per type (deduped, first-seen order) — a type can
    // legitimately hold multiple values (e.g. Cut = skinless AND head-off).
    attributes: groupAttributesByType(variant.attributes),
  };
}

export function appendRow(rows: EditableVariantRow[]): EditableVariantRow[] {
  return [...rows, createEmptyRow()];
}

export function prependRow(rows: EditableVariantRow[]): EditableVariantRow[] {
  return [createEmptyRow(), ...rows];
}

export function insertRowAt(
  rows: EditableVariantRow[],
  index: number,
  position: 'above' | 'below'
): EditableVariantRow[] {
  const at = position === 'above' ? index : index + 1;
  const next = [...rows];
  next.splice(at, 0, createEmptyRow());
  return next;
}

export function removeRowAt(rows: EditableVariantRow[], index: number): EditableVariantRow[] {
  return rows.filter((_, i) => i !== index);
}

// Removes a row by clientId rather than array index. Used for saved-row
// deletion (the trash button on a persisted variant): unlike removeRowAt,
// callers apply this to BOTH `rows` and `original` so the two stay the same
// length and hasUnsavedChanges doesn't permanently short-circuit on a length
// mismatch after a successful delete. Index-based removal is unsafe here
// because the confirming callback can fire after a background reseed has
// reordered/reminted rows — a clientId survives that, a captured index
// doesn't.
export function removeRowByClientId(rows: EditableVariantRow[], clientId: string): EditableVariantRow[] {
  return rows.filter((r) => r.clientId !== clientId);
}

function cloneAsNewRow(source: EditableVariantRow): EditableVariantRow {
  return {
    ...source,
    clientId: nextClientId(),
    id: undefined,
    // Deep-copy attributes: the duplicate must be editable independently of
    // the row it was copied from. Each type now holds an array, so a shallow
    // `{ ...source.attributes }` isn't enough — copy every per-type array too.
    attributes: Object.fromEntries(Object.entries(source.attributes).map(([key, values]) => [key, [...values]])),
  };
}

// Inserts a copy of rows[index] directly below it — same field values, fresh
// clientId, no persisted id (it's a new row), attributes copied by value.
export function duplicateRowAt(rows: EditableVariantRow[], index: number): EditableVariantRow[] {
  const next = [...rows];
  next.splice(index + 1, 0, cloneAsNewRow(rows[index]));
  return next;
}

// Appends a new row prefilled from the last row (same copy semantics as
// duplicateRowAt). An empty list has nothing to prefill from, so it falls
// back to a plain blank row.
export function appendRowFrom(rows: EditableVariantRow[]): EditableVariantRow[] {
  if (rows.length === 0) return [createEmptyRow()];
  return [...rows, cloneAsNewRow(rows[rows.length - 1])];
}

export function isRowEmpty(row: EditableVariantRow): boolean {
  return (
    row.state === '' &&
    row.brand === '' &&
    row.origin === '' &&
    row.baseUnit === '' &&
    row.itemUnit === '' &&
    row.netContent === '' &&
    row.packUnit === '' &&
    row.packSize === '' &&
    row.spec === '' &&
    row.isActive === true &&
    Object.values(row.attributes).every((values) => values.length === 0) &&
    row.id === undefined
  );
}

export function validateRows(rows: EditableVariantRow[], original: EditableVariantRow[] = []): RowValidationError[] {
  const originalByClientId = new Map(original.map((row) => [row.clientId, row]));
  const errors: RowValidationError[] = [];
  rows.forEach((row, rowIndex) => {
    if (isRowEmpty(row)) return;
    const pristine = originalByClientId.get(row.clientId);
    if (pristine && rowSignature(pristine) === rowSignature(row)) return;
    const messages: string[] = [];
    if (!row.state.trim()) messages.push(REQUIRED_MESSAGES.state);
    if (!row.brand.trim()) messages.push(REQUIRED_MESSAGES.brand);
    if (!row.baseUnit.trim()) messages.push(REQUIRED_MESSAGES.baseUnit);
    if (row.netContent.trim() && !(Number.isFinite(Number(row.netContent)) && Number(row.netContent) > 0))
      messages.push(`${UOM_HELP.netContent.label} must be a positive number`);
    if (row.packSize.trim() && !(Number.isInteger(Number(row.packSize)) && Number(row.packSize) > 0))
      messages.push(`${UOM_HELP.packSize.label} must be a positive whole number`);
    if (messages.length) errors.push({ clientId: row.clientId, rowIndex, messages });
  });
  return errors;
}

// Normalizes a row for equality/dirty comparisons: drops clientId (not part
// of the data), and sorts each attribute type's values (plus the type keys
// themselves) so reordering a multi-select's selection — or the order types
// were picked in — never marks an otherwise-unchanged row dirty. Display
// order in the UI is untouched; only this comparison string is normalized.
function rowSignature({ clientId: _clientId, attributes, ...rest }: EditableVariantRow): string {
  const normalizedAttributes = Object.keys(attributes)
    .sort()
    .reduce<Record<string, string[]>>((acc, key) => {
      acc[key] = [...attributes[key]].sort();
      return acc;
    }, {});
  return JSON.stringify({ ...rest, attributes: normalizedAttributes });
}

export function buildBulkPayload(
  rows: EditableVariantRow[],
  original: EditableVariantRow[] = []
): BulkUpsertVariantRow[] {
  const originalByClientId = new Map(original.map((row) => [row.clientId, row]));
  return rows
    .filter(
      (row) => !isRowEmpty(row) && row.state.trim() !== '' && row.brand.trim() !== '' && row.baseUnit.trim() !== ''
    )
    .filter((row) => {
      const pristine = originalByClientId.get(row.clientId);
      return !pristine || rowSignature(pristine) !== rowSignature(row);
    })
    .map((row) => {
      const payload: BulkUpsertVariantRow = {
        state: row.state,
        brand: row.brand,
        origin: row.origin.trim() ? row.origin.trim() : null,
        baseUnit: row.baseUnit,
        itemUnit: row.itemUnit || undefined,
        netContent: row.netContent.trim() ? Number(row.netContent) : undefined,
        packUnit: row.packUnit || undefined,
        packSize: row.packSize.trim() ? Number(row.packSize) : undefined,
        // null, not undefined: the BE reads an omitted key as "leave untouched"
        // and only clears the column on an explicit null. `undefined` is dropped
        // by JSON.stringify, so it could never clear a spec. Same contract as
        // origin above.
        spec: row.spec || null,
        isActive: row.isActive,
        attributes: flattenAttributes(row.attributes),
      };
      if (row.id !== undefined) payload.id = row.id;
      return payload;
    });
}

export function hasUnsavedChanges(current: EditableVariantRow[], original: EditableVariantRow[]): boolean {
  // Uses rowSignature (not a raw strip+stringify) so this stays consistent with
  // validateRows/buildBulkPayload: reordering a multi-select's selection alone
  // must not read as "unsaved changes" here either.
  if (current.length !== original.length) return true;
  return current.some((row, i) => rowSignature(row) !== rowSignature(original[i]));
}
